import { motion } from "framer-motion";
import { Star, ShieldCheck, MapPin, FileCheck } from "lucide-react";

const GBP_URL = "https://maps.google.com/?cid=4748127638390652358";

const TRUST = [
  { icon: Star, h: "5.0 on Google", p: "Every review on our Google Business Profile is from a real customer. Read them in full on Google — we don't paraphrase or invent quotes here." },
  { icon: ShieldCheck, h: "Insured · 30-day warranty", p: "Public liability insured, fixed price agreed before we book, and a 30-day peace-of-mind warranty on every calibration." },
  { icon: FileCheck, h: "Logged, not promised", p: "Before/after data-logs on every job. Your original ECU file is archived so the car can go back to standard at any time." },
  { icon: MapPin, h: "We come to you", p: "Nottingham, Derby and 17 East Midlands towns — your driveway, workplace or yard. No workshop, no waiting list." },
];

export default function Testimonials() {
  return (
    <section className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10" data-testid="testimonials-section">
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt">◉ Reviews &amp; trust</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Rated 5.0 by drivers <br />
              <span className="text-white/40">across the East Midlands.</span>
            </h2>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1" aria-label="5 out of 5 stars">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-5 h-5 text-volt fill-volt" />)}
              </span>
              <a href={GBP_URL} target="_blank" rel="noopener noreferrer" className="font-mono text-xs uppercase tracking-widest text-volt hover:text-white transition-colors" data-testid="google-reviews-read-link">
                Read our Google reviews →
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-[1px] bg-white/10">
          {TRUST.map((t, i) => (
            <motion.div
              key={t.h}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="col-span-12 md:col-span-6 lg:col-span-3 bg-carbon p-8 md:p-10"
              data-testid={`trust-${i}`}
            >
              <t.icon className="w-6 h-6 text-volt mb-6" strokeWidth={1.5} />
              <h3 className="font-display uppercase text-xl md:text-2xl leading-tight mb-3">{t.h}</h3>
              <p className="font-body text-white/65 text-sm md:text-base leading-relaxed">{t.p}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 border border-white/10 bg-surface-1 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-volt mb-2">◉ Recently tuned with us?</div>
            <div className="font-display uppercase text-2xl md:text-3xl leading-tight">A quick review helps other drivers find us.</div>
            <p className="font-body text-white/60 mt-3 max-w-xl">Two minutes on Google is the best thank-you we could ask for — and it tells the next Nottingham or Derby driver what to expect.</p>
          </div>
          <a href={GBP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-volt text-black px-6 py-4 font-mono text-xs uppercase tracking-widest font-semibold hover:bg-white transition-colors shrink-0" data-testid="google-review-link">
            Leave a review →
          </a>
        </div>
      </div>
    </section>
  );
}
